import { PROMPT_OVERVIEW_TAB_PARAM } from "./promptVersionLogsReturnNavigation";

import {
  PROMPT_ANALYTICS_URL_NAMESPACE,
  REQUESTS_SIDEBAR_URL_NAMESPACE,
  REQUESTS_TABLE_URL_NAMESPACE,
  type StructuredSearchUrlParamKeys,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { URL_PARAM_KEYS } from "@/utils/utils";

export const PROMPT_VERSION_LOGS_SEARCH_CONTEXT = "promptVersionLogs";

export interface PromptVersionLogsSearchContext {
  promptId: number;
  promptVersionNumber: number;
}

/** Params owned by prompt Analytics; cleared only when switching prompt/version. */
const SCOPED_SEARCH_PARAM_KEYS = [
  URL_PARAM_KEYS.SEARCH_CONTEXT,
  URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_ID,
  URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_VERSION_NUMBER,
  PROMPT_ANALYTICS_URL_NAMESPACE.filters,
  PROMPT_ANALYTICS_URL_NAMESPACE.q,
  PROMPT_ANALYTICS_URL_NAMESPACE.page,
  PROMPT_ANALYTICS_URL_NAMESPACE.pageSize,
  PROMPT_ANALYTICS_URL_NAMESPACE.sortBy,
  PROMPT_ANALYTICS_URL_NAMESPACE.sortDesc,
  PROMPT_ANALYTICS_URL_NAMESPACE.startTime,
  PROMPT_ANALYTICS_URL_NAMESPACE.endTime,
  // Legacy global keys still cleared on version change (migration).
  REQUESTS_TABLE_URL_NAMESPACE.filters,
  REQUESTS_TABLE_URL_NAMESPACE.q,
  REQUESTS_TABLE_URL_NAMESPACE.page,
  REQUESTS_TABLE_URL_NAMESPACE.pageSize,
  REQUESTS_TABLE_URL_NAMESPACE.sortBy,
  REQUESTS_TABLE_URL_NAMESPACE.sortDesc,
  URL_PARAM_KEYS.STRUCTURED_FILTERS,
  URL_PARAM_KEYS.Q,
  URL_PARAM_KEYS.PAGE,
  URL_PARAM_KEYS.PAGE_SIZE,
  URL_PARAM_KEYS.REQUESTS_START_TIME,
  URL_PARAM_KEYS.REQUESTS_END_TIME,
  URL_PARAM_KEYS.START_TIME,
  URL_PARAM_KEYS.END_TIME,
  "sortBy",
  "sortDesc",
  PROMPT_OVERVIEW_TAB_PARAM,
];

/** Old global keys → prompt Analytics namespace only (never copies into sidebar/table). */
const LEGACY_TO_PROMPT_PARAM_MIGRATIONS: {
  field: keyof StructuredSearchUrlParamKeys;
  legacyKeys: string[];
}[] = [
  {
    field: "filters",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.filters, URL_PARAM_KEYS.STRUCTURED_FILTERS],
  },
  {
    field: "q",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.q, URL_PARAM_KEYS.Q],
  },
  {
    field: "page",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.page, URL_PARAM_KEYS.PAGE],
  },
  {
    field: "pageSize",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.pageSize, URL_PARAM_KEYS.PAGE_SIZE],
  },
  {
    field: "sortBy",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.sortBy, "sortBy"],
  },
  {
    field: "sortDesc",
    legacyKeys: [REQUESTS_TABLE_URL_NAMESPACE.sortDesc, "sortDesc"],
  },
  {
    field: "startTime",
    legacyKeys: [URL_PARAM_KEYS.REQUESTS_START_TIME, URL_PARAM_KEYS.START_TIME],
  },
  {
    field: "endTime",
    legacyKeys: [URL_PARAM_KEYS.REQUESTS_END_TIME, URL_PARAM_KEYS.END_TIME],
  },
];

export const parsePromptVersionLogsSearchContext = (
  params: URLSearchParams,
): PromptVersionLogsSearchContext | null => {
  if (params.get(URL_PARAM_KEYS.SEARCH_CONTEXT) !== PROMPT_VERSION_LOGS_SEARCH_CONTEXT) {
    return null;
  }

  const promptId = Number(params.get(URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_ID));
  const promptVersionNumber = Number(
    params.get(URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_VERSION_NUMBER),
  );
  if (!Number.isFinite(promptId) || !Number.isFinite(promptVersionNumber)) {
    return null;
  }

  return { promptId, promptVersionNumber };
};

export const isPromptVersionLogsSearchContextValid = (
  params: URLSearchParams,
  context: PromptVersionLogsSearchContext,
): boolean => {
  const stored = parsePromptVersionLogsSearchContext(params);
  return (
    !!stored &&
    stored.promptId === context.promptId &&
    stored.promptVersionNumber === context.promptVersionNumber
  );
};

const writeSearchContextPointer = (
  params: URLSearchParams,
  context: PromptVersionLogsSearchContext,
): void => {
  params.set(URL_PARAM_KEYS.SEARCH_CONTEXT, PROMPT_VERSION_LOGS_SEARCH_CONTEXT);
  params.set(URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_ID, String(context.promptId));
  params.set(
    URL_PARAM_KEYS.SEARCH_CONTEXT_PROMPT_VERSION_NUMBER,
    String(context.promptVersionNumber),
  );
};

/**
 * First visit: stamp searchContext. Legacy globals belong to table/sidebar only —
 * never promote them into prompt Analytics. If the sidebar namespace is missing those
 * fields, remap legacy keys into sidebar params so the open drawer keeps its filters,
 * then strip the globals from the prompt URL.
 */
export const migratePromptVersionLogsSearchContextParams = (
  params: URLSearchParams,
  context: PromptVersionLogsSearchContext,
): URLSearchParams => {
  const next = new URLSearchParams(params);

  for (const { field, legacyKeys } of LEGACY_TO_PROMPT_PARAM_MIGRATIONS) {
    const sidebarKey = REQUESTS_SIDEBAR_URL_NAMESPACE[field];
    if (!next.has(sidebarKey)) {
      for (const legacyKey of legacyKeys) {
        if (next.has(legacyKey)) {
          next.set(sidebarKey, next.get(legacyKey)!);
          break;
        }
      }
    }
    for (const legacyKey of legacyKeys) {
      next.delete(legacyKey);
    }
  }

  writeSearchContextPointer(next, context);
  return next;
};

/** Version/prompt change: drop scoped Analytics state and write a fresh context pointer. */
export const resetPromptVersionLogsSearchContextParams = (
  params: URLSearchParams,
  context: PromptVersionLogsSearchContext,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  for (const key of SCOPED_SEARCH_PARAM_KEYS) {
    next.delete(key);
  }
  writeSearchContextPointer(next, context);
  return next;
};
